import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

//  a function to convert big number into brief number
// e.g. 1000000 -> 1M
export function formatBigNumber(
  num: number | null | string | undefined
): string {
  num = toNumber(num);
  if (num >= 1000000000) {
    // e.g. 8131234567.54 -> 8.1B
    return (num / 1000000000).toFixed(1) + "B";
  }
  if (num >= 1000000) {
    // e.g. 8131234.54 -> 8.1M
    return (num / 1000000).toFixed(1) + "M";
  }

  if (num >= 10000) {
    // e.g. 30124 -> 30.1K
    // e.g. 230124.54 -> 230.1K
    return (num / 1000).toFixed(1) + "K";
  }

  return num.toString();
}

/**
 * Display weight based on user's preferred_weight_unit
 */

export function displayWeight(
  weight_lb: number | null,
  weight_kg: number | null,
  preferred_weight_unit: string = "lb"
): string | number {
  if (!preferred_weight_unit) return "";

  const useWeight = preferred_weight_unit == "lb" ? weight_lb : weight_kg;

  return useWeight ? formatNumber(useWeight) + " " + preferred_weight_unit : "";
}

/**
 * Format a number in a readable format
 * e.g., "12.3456" -> "12.35" (12.3456 -> "12.35")
 * e.g., "123456" -> "123,456" (123456 -> "123,456")
 * @param num - Number to format
 * @returns Formatted number string
 */
export function formatNumber(num: number): string {
  return new Intl.NumberFormat("en-US").format(num);
}

/**
 * Formats a number to have at most 1 digit after the decimal point for floats,
 * and returns integers as integers (without decimal point).
 *
 * @param {number} num - The number to format
 * @returns {number} Formatted number: number with 1 decimal place for floats, number for integers
 * e.g., 1234 => 1234
 * e.g., 1234.567 => 1234.5
 */
export function formatFloat(num: number, digits: number = 1): number {
  // Check if the number is an integer
  // - true: 0, 123, -123
  // - false: 123.456, "123", NaN
  if (Number.isInteger(num)) {
    return num;
  }

  // For floats, round to 1 decimal place
  return parseFloat(Number(num).toFixed(digits));
}

/**
 * Converts a value to a number.
 * @param value The value to convert
 * @returns The converted number
 */
export function toNumber(value: string | number | null | undefined): number {
  if (typeof value === "number") return value;

  if (!value) return 0;

  const tryParseInt = parseInt(value || "0", 0);

  if (!isNaN(tryParseInt)) return tryParseInt;

  return 0;
}
